// ============================================================================
// BOQ — rate age / reliability classification
// ============================================================================
// Even after inflation adjustment, an older rate observation carries more
// uncertainty (market structure shifts, spec drift, and the cost index itself
// is only approximate). This classifies a rate's age into a tier so the UI can
// show a glanceable cue next to the rate date — see components/RateAgePill.
// ============================================================================

const YEAR_MS = 365.25 * 24 * 3600 * 1000;

const TIERS = {
  fresh:  { color: '#3f7d4f', note: 'Recent — high confidence.' },
  recent: { color: '#7a8b3a', note: 'Reasonably current.' },
  aging:  { color: '#b8860b', note: 'Ageing — sanity-check against the current market.' },
  stale:  { color: '#a94442', note: 'Old — verify against a recent quote.' },
};

// Returns null for a missing/invalid date, otherwise
// { years, tier, label, color, tooltip }.
export function rateAge(rateDate, now = new Date()) {
  if (!rateDate) return null;
  const d = rateDate instanceof Date ? rateDate : new Date(rateDate);
  if (Number.isNaN(d.getTime())) return null;

  const years = (now.getTime() - d.getTime()) / YEAR_MS;

  let tier;
  if (years < 1) tier = 'fresh';
  else if (years < 2) tier = 'recent';
  else if (years < 4) tier = 'aging';
  else tier = 'stale';

  // Compact label: months under a year, else years to one decimal (whole at 10+).
  let label;
  if (years < 0) label = 'new';
  else if (years < 1) label = `${Math.max(1, Math.round(years * 12))}mo`;
  else label = `${years < 10 ? years.toFixed(1) : Math.round(years)}y`;

  const { color, note } = TIERS[tier];
  const ymd = d.toISOString().slice(0, 10);
  // Self-explanatory rollover: what the tag means, the age, and the reliability
  // implication — older source rates are less reliable even after adjustment.
  const tooltip = `Source-rate age: ~${label} (recorded ${ymd}). Older rates are less reliable even after inflation adjustment. ${note}`;

  return { years, tier, label, color, tooltip };
}
